import React from 'react';
import { useLocalizedStrings } from '../../Localization';
import { BreadcrumbProps, Button, Icon, NumberDisplay, Tooltip } from '@aidkitorg/component-library';
import { useNotifications } from '../../Contexts/NotificationsContext';
import { NotificationsDataTable } from './components/NotificationsDataTable';
import { AdminPanelPage } from '../AdminPanelPage';
import { useNotificationsOverviewConfig } from '../shared/hooks/useAdminPanelPageConfig';

export const NotificationsPage = () => {
  const L = useLocalizedStrings()

  const {
    fetchNotificationsOverview,
    enabledNotifications,
    notifications,
    notificationsDataTimestamp,

    isNotificationsLoading
  } = useNotifications();

  // Fetch page configuration using the hook
  const {
    pageConfig,
    isLoading: isConfigLoading,
    getLocalizedText,
    isVisible
  } = useNotificationsOverviewConfig();

  // Count notifications with SQL errors
  const notificationsWithErrors = notifications.filter(notification =>
    notification.sqlError || (notification.expandableRows?.some(row => row.sqlError))
  ).length;

  // Use config to determine what to show
  const showEnabledNotifications = isVisible(['summaryMetrics', 'enabledNotifications']);
  const showSqlErrors = isVisible(['summaryMetrics', 'sqlErrors']);
  const pageTitle = getLocalizedText(['pageHeader', 'title'], L.admin_panel.nav.notifications);
  const pageDescription = getLocalizedText(['pageHeader', 'description'], L.admin_panel.notifications.description);

  const enabledNotificationsCard = showEnabledNotifications ? (
    <NumberDisplay
      loading={isNotificationsLoading}
      className="sm:max-w-64 min-w-64"
      title={L.admin_panel.notifications.enabled_notifications}
      value={enabledNotifications}
      iconName="BellIcon"
      iconColor="blue"
    />
  ) : null;

  const sqlErrorsCard = notificationsWithErrors > 0 && showSqlErrors ? (
    <NumberDisplay
      loading={isNotificationsLoading}
      className="sm:max-w-64 min-w-64"
      title={L.admin_panel.notifications.sql_errors}
      value={notificationsWithErrors}
      iconName="ExclamationTriangleIcon"
      iconColor="red"
    />
  ) : null;

  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: pageTitle,
    }
  ];

  return (
    <AdminPanelPage
      title={pageTitle}
      description={pageDescription}
      breadcrumbs={breadcrumbs}
      pageActionButtons={[
        <Tooltip text={L.admin_panel.payments.refresh_data} asChild>
          <Button
            size="sm"
            variant='secondary'
            onClick={() => { fetchNotificationsOverview(true) }}
            icon={<Icon role="decorative" type="solid" name="ArrowPathIcon" />}
            aria-label={L.admin_panel.payments.refresh_data}
          />
        </Tooltip>
      ]}
    >
      <div className="flex flex-col gap-2 sm:gap-4 w-full">
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
          {enabledNotificationsCard}
          {sqlErrorsCard}
        </div>
        <div>
          <NotificationsDataTable
            lastUpdatedTimestamp={notificationsDataTimestamp}
            pageConfig={pageConfig}
            isConfigLoading={isConfigLoading}
          />
        </div>
      </div>
    </AdminPanelPage>
  )
}
